import React, { useEffect, useRef, useState } from 'react'
import { array, arrayOf, func, object, shape, string } from 'prop-types'

import useDeviceDetect from '@sputnik/hooks/useDeviceDetect'
import useGetRequiredDocs from '@sputnik/hooks/useRequiredDocs'
import { logError } from '@sputnik/utils/loggerUtils'

import DocumentUploadTypeModal from './DocumentUploadTypeModal'
import LoadingLogo from './LoadingLogo'
import MobileSourceUploadSelector from './MobileSourceUploadSelector'
import { useRequestDocumentUploadToken } from './RequestDocumentUploadToken.gql'
import { getFormattedOwnerNames } from './requiredDocumentMapper'
import UploaderTile from './UploaderTile'

const UploaderTileList = ({ creditApp, remainingDocs, requiredDocuments, setRemainingDocs }) => {
  const [isDocumentTypeUploadModalShowing, setIsDocumentTypeUploadModalShowing] = useState(false)
  const [mobileDocumentUploadToken, setMobileDocumentUploadToken] = useState('')
  const [selectedDocument, setSelectedDocument] = useState({})
  const [requestDocumentUploadToken] = useRequestDocumentUploadToken()
  const { isMobile } = useDeviceDetect()
  const [wasUploaderShownOnce, setWasUploaderShownOnce] = useState(false)
  const requiredDocsPollerRef = useRef()
  const [isUploading, setIsUploading] = useState(false)
  const appId = creditApp?.salesforceCreditId
  const authorizedSigner = creditApp?.authorizedSigner
  const beneficialOwners = creditApp?.beneficialOwners
  const accountNumberLastFour = creditApp?.financeInfo?.bankInfo?.accountNumberLastFour
  const desiredCredit = creditApp?.financeInfo?.desiredCredit

  const formattedNames = getFormattedOwnerNames(beneficialOwners, authorizedSigner)

  const imageBtnRef = useRef()

  const [getRequiredDocs, { refetch }] = useGetRequiredDocs(appId)

  // TODO: get rid of this "polling" as it's not efficient. . . use a refectch instead from the useQuery.
  useEffect(() => {
    if (wasUploaderShownOnce && !requiredDocsPollerRef?.current) {
      getRequiredDocs().then(() => {
        requiredDocsPollerRef.current = setInterval(async () => {
          const { data } = await refetch()
          const currentRemainingDocs = data?.creditApplicationBypassingSf?.requiredDocs?.map((doc) => doc.documentType)
          const changesDetected = JSON.stringify(currentRemainingDocs?.sort()) !== JSON.stringify(remainingDocs?.sort())

          if (currentRemainingDocs && changesDetected) {
            setRemainingDocs(currentRemainingDocs)

            if (!currentRemainingDocs.includes(selectedDocument?.selectedDocumentType)) {
              setIsDocumentTypeUploadModalShowing(false)
            }
          }
        }, 1500)
      })
    }

    return () => {
      clearInterval(requiredDocsPollerRef?.current)
      requiredDocsPollerRef.current = null
    }
  }, [selectedDocument, getRequiredDocs, refetch, remainingDocs, setRemainingDocs, wasUploaderShownOnce, appId])

  const onTileSelect = async (selectedDocumentType, selectedDocumentUuid) => {
    setSelectedDocument({ selectedDocumentType, selectedDocumentUuid })

    if (isMobile) {
      setWasUploaderShownOnce(true)
      imageBtnRef.current.click()
      return
    }

    return requestDocumentUploadToken(appId)
      .then((result) => {
        const documentToken = result?.data?.requestDocumentUploadToken?.token || ''
        setMobileDocumentUploadToken(documentToken)
      })
      .catch((e) => {
        logError(e, {
          attributes: {
            action: 'requestMobileDocumentUploadToken',
            creditAppId: appId,
            message: 'Error while generating mobile document upload token',
            result: e,
          },
          eventName: 'UploadDocument',
        })
      })
      .finally(() => {
        setIsDocumentTypeUploadModalShowing(true)
        setWasUploaderShownOnce(true)
      })
  }

  return (
    <>
      <LoadingLogo isShowing={isUploading && isMobile} />
      {requiredDocuments?.map(({ documentType, documentUuid, statusMessage }) => (
        <div key={documentUuid}>
          <UploaderTile
            accountNumberLastFour={accountNumberLastFour}
            appId={appId}
            desiredCredit={desiredCredit}
            documentType={documentType}
            documentUuid={documentUuid}
            formattedNames={formattedNames}
            isMobile={isMobile}
            remainingDocs={remainingDocs}
            setRemainingDocs={setRemainingDocs}
            statusMessage={statusMessage}
            onSelect={() => onTileSelect(documentType, documentUuid)}
          />
        </div>
      ))}
      {!isMobile && (
        <DocumentUploadTypeModal
          appId={appId}
          isShowing={isDocumentTypeUploadModalShowing}
          selectedDocument={selectedDocument}
          setIsModalShowing={setIsDocumentTypeUploadModalShowing}
          token={mobileDocumentUploadToken}
        />
      )}
      {isMobile && (
        <MobileSourceUploadSelector
          appId={appId}
          imageBtnRef={imageBtnRef}
          remainingDocs={remainingDocs}
          selectedDocument={selectedDocument}
          setIsUploading={setIsUploading}
        />
      )}
    </>
  )
}

UploaderTileList.propTypes = {
  creditApp: object,
  remainingDocs: arrayOf(
    shape({
      documentType: string,
      documentUuid: string,
      statusMessage: string,
    }),
  ),
  requiredDocuments: array,
  setRemainingDocs: func,
}

export default UploaderTileList
